import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/Button';
import TextInput from '../ui/TextInput';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../ui/Modal';
import ConfirmationDialog from '../ui/ConfirmationDialog';
import {
  FilterIcon,
  XIcon,
  CopyIcon,
  TrashIcon,
  CheckIcon,
  PencilIcon,
  CheckCircleIcon,
  InfoIcon,
} from '../ui/Icons';
import { UnifiedFilter } from './FilterSection';
import useSWR from 'swr';
import {
  getFilterPresets,
  createFilterPreset,
  updateFilterPreset,
  deleteFilterPreset,
  setDefaultFilterPreset,
  FilterPreset as APIFilterPreset,
} from '../../services/filterPresetsService';
import { useNotification } from '../../contexts/NotificationContext';
import { convertUnifiedFiltersToLegacyFormat, normalizeFiltersForPersistence } from './filterUtils';

interface FilterPreset extends APIFilterPreset {}

interface FilterPresetsProps {
  currentFilters: Record<string, UnifiedFilter>;
  onApplyFilters: (filters: Record<string, UnifiedFilter>) => void;
  onPresetsChange?: () => void;
  tableId?: string;
  entityName?: string;
  isOpen: boolean;
  onClose: () => void;
  // For calculating filtered record counts
  data?: any[];
  applyFilter?: (
    items: any[],
    filters: any,
    dateRanges?: Record<string, { start: Date | null; end: Date | null }>
  ) => any[];
  allFilterKeys?: string[];
  textFilterKeys?: string[];
}

const FilterPresets: React.FC<FilterPresetsProps> = ({
  currentFilters = {},
  onApplyFilters,
  onPresetsChange,
  tableId = 'default-table',
  entityName = 'Table',
  isOpen,
  onClose,
  data = [],
  applyFilter,
  allFilterKeys = [],
  textFilterKeys = [],
}) => {
  const { colors } = useTheme();
  const { showNotification } = useNotification();
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [showInQuickFilters, setShowInQuickFilters] = useState(false);
  const [editingPreset, setEditingPreset] = useState<FilterPreset | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState<FilterPreset | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load presets from API using SWR
  const { data: presets = [], mutate: mutatePresets, isLoading: isLoadingPresets } = useSWR<FilterPreset[]>(
    isOpen ? `filter-presets-${tableId}` : null,
    () => getFilterPresets(tableId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  // Close on outside click (but not when modal is open)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if modal is open
      if (showSaveModal || showDeleteConfirm) {
        return;
      }
      
      // Check if click is outside the container
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Also check if click is not on a modal
        const target = event.target as HTMLElement;
        if (!target.closest('[role="dialog"]') && !target.closest('.fixed.inset-0')) {
          onClose();
        }
      }
    };

    if (isOpen && !showSaveModal && !showDeleteConfirm) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, showSaveModal, showDeleteConfirm]);

  // Get current filter configuration
  const getCurrentFilters = () => currentFilters;

  // Save current filters as a new preset
  const handleSavePreset = async () => {
    if (!presetName.trim()) return;

    // Normalize so relative date presets (overdue, thisMonth, etc.) store only the preset
    // name; dates are resolved when the preset is applied (avoids stale "overdue" dates)
    const filtersToSave = normalizeFiltersForPersistence(getCurrentFilters());
    console.log('[FilterPresets] Saving preset:', presetName.trim());
    console.log('[FilterPresets] Editing preset:', editingPreset?.name || 'new');
    console.log('[FilterPresets] Filters being saved:', JSON.stringify(filtersToSave, null, 2));
    if (editingPreset) {
      console.log('[FilterPresets] Original preset filters:', JSON.stringify(editingPreset.filters, null, 2));
    }

    setIsLoading(true);
    try {
      if (editingPreset) {
        // Update existing preset - use current filters (which should be the preset's filters if user didn't change them)
        await updateFilterPreset(editingPreset.id, {
          name: presetName.trim(),
          description: presetDescription.trim() || undefined,
          filters: filtersToSave, // This will be the preset's filters since we applied them in startEditPreset
          showInQuickFilters: showInQuickFilters,
        }, tableId);
        showNotification('Filter preset updated successfully', 'success');
      } else {
        // Create new preset
        await createFilterPreset({
          moduleName: tableId,
          name: presetName.trim(),
          description: presetDescription.trim() || undefined,
          filters: filtersToSave,
          isDefault: false,
          showInQuickFilters: showInQuickFilters,
        });
        showNotification('Filter preset saved successfully', 'success');
      }

      // Refresh presets list
      await mutatePresets();
      if (onPresetsChange) {
        onPresetsChange();
      }

      setShowSaveModal(false);
      setPresetName('');
      setPresetDescription('');
      setShowInQuickFilters(false);
      setEditingPreset(null);
    } catch (error: any) {
      console.error('Error saving filter preset:', error);
      showNotification(
        error.response?.data?.message || 'Failed to save filter preset',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Delete a preset
  const handleDeletePreset = async () => {
    if (!presetToDelete) return;

    setIsLoading(true);
    try {
      await deleteFilterPreset(presetToDelete.id, tableId);
      showNotification('Filter preset deleted successfully', 'success');
      
      // Refresh presets list
      await mutatePresets();
      if (onPresetsChange) {
        onPresetsChange();
      }

      setShowDeleteConfirm(false);
      setPresetToDelete(null);
    } catch (error: any) {
      console.error('Error deleting filter preset:', error);
      showNotification(
        error.response?.data?.message || 'Failed to delete filter preset',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Apply a preset
  const handleApplyPreset = (preset: FilterPreset) => {
    console.log('[FilterPresets] Applying preset:', preset.name);
    console.log('[FilterPresets] Preset filters:', JSON.stringify(preset.filters, null, 2));
    console.log('[FilterPresets] Current filters before apply:', JSON.stringify(currentFilters, null, 2));
    onApplyFilters(preset.filters);
    onClose();
  };

  // Start editing a preset
  const startEditPreset = (preset: FilterPreset) => {
    setEditingPreset(preset);
    setPresetName(preset.name);
    setPresetDescription(preset.description || '');
    setShowInQuickFilters(preset.showInQuickFilters || false);
    // Apply the preset's filters so user can see/edit them
    onApplyFilters(preset.filters);
    setShowSaveModal(true);
  };

  // Confirm delete
  const confirmDeletePreset = (preset: FilterPreset) => {
    setPresetToDelete(preset);
    setShowDeleteConfirm(true);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setPresetToDelete(null);
  };

  // Default preset management
  const getDefaultPresetId = () => {
    const defaultPreset = presets.find((p) => p.isDefault);
    return defaultPreset ? defaultPreset.id : null;
  };

  const handleSetAsDefault = async (presetId: number) => {
    setIsLoading(true);
    try {
      await setDefaultFilterPreset(presetId);
      showNotification('Default preset updated successfully', 'success');
      
      // Refresh presets list
      await mutatePresets();
      if (onPresetsChange) {
        onPresetsChange();
      }
    } catch (error: any) {
      console.error('Error setting default preset:', error);
      showNotification(
        error.response?.data?.message || 'Failed to set default preset',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveDefault = async () => {
    const defaultPreset = presets.find((p) => p.isDefault);
    if (!defaultPreset) return;

    setIsLoading(true);
    try {
      // Unset default by updating the preset
      await updateFilterPreset(defaultPreset.id, { isDefault: false }, tableId);
      showNotification('Default preset removed successfully', 'success');
      
      // Refresh presets list
      await mutatePresets();
      if (onPresetsChange) {
        onPresetsChange();
      }
    } catch (error: any) {
      console.error('Error removing default preset:', error);
      showNotification(
        error.response?.data?.message || 'Failed to remove default preset',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Check if current filters match a preset
  const isPresetActive = (preset: FilterPreset) => {
    return JSON.stringify(preset.filters) === JSON.stringify(getCurrentFilters());
  };

  // Check if there are active filters
  const hasActiveFilters = () => {
    const filters = getCurrentFilters();
    return Object.entries(filters).some(([key, filterConfig]) => {
      if (filterConfig.type === 'multiSelect') return filterConfig.values && filterConfig.values.length > 0;
      if (filterConfig.type === 'dateRange') return filterConfig.from || filterConfig.to || filterConfig.preset;
      if (filterConfig.type === 'numericRange') return filterConfig.min !== undefined || filterConfig.max !== undefined;
      if (filterConfig.type === 'boolean') return filterConfig.value !== undefined;
      if (filterConfig.type === 'text') return filterConfig.textValue && filterConfig.textValue.trim() !== '';
      return filterConfig.textValue;
    });
  };

  // Calculate filtered record count for a preset
  const getFilteredCount = (filters: Record<string, UnifiedFilter>): number | null => {
    if (!applyFilter || !data || data.length === 0) return null;
    
    try {
      const { filters: legacyFilters, dateRanges } = convertUnifiedFiltersToLegacyFormat(
        filters,
        allFilterKeys,
        textFilterKeys
      );
      const filtered = applyFilter(data, legacyFilters, dateRanges);
      return filtered.length;
    } catch (error) {
      console.error('Error calculating filtered count:', error);
      return null;
    }
  };

  // Get filter summary for display
  const getFilterSummary = (filters: Record<string, UnifiedFilter>) => {
    // Try to get filtered count first
    const filteredCount = getFilteredCount(filters);
    if (filteredCount !== null) {
      return `${filteredCount.toLocaleString()} record${filteredCount !== 1 ? 's' : ''}`;
    }
    
    // Fallback to filter count if we can't calculate
    const activeFilters = Object.entries(filters || {}).filter(([key, filterConfig]) => {
      if (filterConfig.type === 'multiSelect') return filterConfig.values && filterConfig.values.length > 0;
      if (filterConfig.type === 'dateRange') return filterConfig.from || filterConfig.to || filterConfig.preset;
      if (filterConfig.type === 'numericRange') return filterConfig.min !== undefined || filterConfig.max !== undefined;
      if (filterConfig.type === 'boolean') return filterConfig.value !== undefined;
      if (filterConfig.type === 'text') return filterConfig.textValue && filterConfig.textValue.trim() !== '';
      return filterConfig.textValue;
    });

    if (activeFilters.length === 0) return 'No filters';
    if (activeFilters.length === 1) return '1 filter';
    return `${activeFilters.length} filters`;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop to keep the panel on top and dim the background */}
      <div
        className="fixed inset-0 z-[95] bg-black/30 dark:bg-black/40"
        onClick={onClose}
        aria-hidden
      />

      <div
        ref={containerRef}
        className={cn(
          'fixed inset-y-4 left-1/2 -translate-x-1/2 max-w-3xl w-[95vw] sm:w-[90vw]',
          'max-h-[80vh] z-[100]',
          'bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700',
          'overflow-hidden flex flex-col'
        )}
      >
        {/* Header */}
        <div className={cn(
          'flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b',
          'bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
          'border-gray-200 dark:border-gray-700 flex-shrink-0'
        )}>
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="p-1.5 sm:p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex-shrink-0">
              <FilterIcon className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={`text-sm sm:text-base font-bold ${colors.text} truncate`}>
                {entityName} Filter Presets
              </h3>
              <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-0.5 hidden sm:block`}>
                Save and manage your filter combinations
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={cn(
              'h-8 w-8 sm:h-9 sm:w-9 p-0 rounded-lg transition-all flex-shrink-0',
              `${colors.textMuted} hover:${colors.textSecondary}`,
              'hover:bg-gray-100 dark:hover:bg-gray-700',
              'flex items-center justify-center ml-2'
            )}
            title="Close"
          >
            <XIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
          {/* Quick Actions */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 sm:p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/10 dark:to-purple-900/10 rounded-lg sm:rounded-xl border border-indigo-100 dark:border-indigo-900/30 mb-3 sm:mb-4">
            <div className="flex-1 min-w-0">
              <h4 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white mb-0.5 sm:mb-1">
                Quick Actions
              </h4>
              <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
                {hasActiveFilters() 
                  ? `Current: ${getFilterSummary(getCurrentFilters())}`
                  : 'Apply some filters first, then save them as a preset'}
              </p>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPresetName('');
                  setPresetDescription('');
                  setShowInQuickFilters(false);
                  setEditingPreset(null);
                  setShowSaveModal(true);
                }}
                disabled={!hasActiveFilters()}
                className={cn(
                  'text-[10px] sm:text-xs bg-white dark:bg-gray-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300',
                  !hasActiveFilters() && 'opacity-50 cursor-not-allowed',
                  'flex-1 sm:flex-initial'
                )}
                title={hasActiveFilters() ? 'Save current filter combination' : 'Apply some filters first to save them'}
              >
                <CopyIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
                Save Current
              </Button>

              {hasActiveFilters() && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    onApplyFilters({});
                    onClose();
                  }}
                  className="text-[10px] sm:text-xs hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 flex-1 sm:flex-initial"
                >
                  <TrashIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
                  Clear All
                </Button>
              )}
            </div>
          </div>

          {/* Saved Presets */}
          <div className="space-y-2 sm:space-y-3">
            {presets.length === 0 ? (
              <div className="text-center py-8 sm:py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg sm:rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700">
                <div className="p-2 sm:p-3 bg-gray-100 dark:bg-gray-700 rounded-full w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                  <FilterIcon className={`w-6 h-6 sm:w-8 sm:h-8 ${colors.textMuted}`} />
                </div>
                <p className={`text-xs sm:text-sm font-semibold ${colors.text} mb-1`}>No saved filter presets</p>
                <p className={`text-[10px] sm:text-xs ${colors.textMuted} mb-3 sm:mb-4`}>
                  Apply some filters and save your first preset
                </p>
                {hasActiveFilters() && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPresetName('');
                      setPresetDescription('');
                      setShowInQuickFilters(false);
                      setEditingPreset(null);
                      setShowSaveModal(true);
                    }}
                    className="text-[10px] sm:text-xs"
                  >
                    <CopyIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
                    Create First Preset
                  </Button>
                )}
              </div>
            ) : (
              presets.map((preset) => {
                const isDefault = preset.isDefault;
                const isActive = isPresetActive(preset);
                return (
                  <div
                    key={preset.id}
                    className={cn(
                      'group relative p-3 sm:p-4 lg:p-5 border-2 rounded-lg sm:rounded-xl transition-all duration-200',
                      isActive
                        ? 'bg-gradient-to-br from-indigo-500 to-purple-600 border-indigo-400 shadow-xl scale-[1.01]'
                        : isDefault
                        ? 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-300 dark:border-green-700 shadow-md hover:shadow-lg hover:scale-[1.005]'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-700 hover:scale-[1.005]'
                    )}
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between gap-3 sm:gap-0">
                      <div className="flex-1 min-w-0 w-full sm:w-auto">
                        <div className="flex items-center gap-2 sm:gap-2.5 mb-2">
                          <div className={cn(
                            'p-1 sm:p-1.5 rounded-lg flex-shrink-0',
                            isActive
                              ? 'bg-white/20'
                              : isDefault
                              ? 'bg-green-200 dark:bg-green-800'
                              : 'bg-indigo-100 dark:bg-indigo-900/30'
                          )}>
                            <FilterIcon className={cn(
                              'w-3.5 h-3.5 sm:w-4 sm:h-4',
                              isActive
                                ? 'text-white'
                                : isDefault
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-indigo-600 dark:text-indigo-400'
                            )} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                              <h4
                                className={cn(
                                  'text-xs sm:text-sm font-bold truncate',
                                  isActive 
                                    ? colors.primaryText
                                    : isDefault 
                                    ? 'text-green-800 dark:text-green-200' 
                                    : colors.text
                                )}
                              >
                                {preset.name}
                              </h4>
                              {isActive && (
                                <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold bg-white/30 text-white backdrop-blur-sm border border-white/20 flex-shrink-0">
                                  Active
                                </span>
                              )}
                              {isDefault && !isActive && (
                                <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200 border border-green-300 dark:border-green-700 flex-shrink-0">
                                  Default
                                </span>
                              )}
                              {preset.showInQuickFilters && (
                                <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold bg-indigo-200 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200 border border-indigo-300 dark:border-indigo-700 flex-shrink-0">
                                  Quick Filter
                                </span>
                              )}
                            </div>
                            {preset.description && (
                              <p
                                className={cn(
                                  'text-[10px] sm:text-xs mt-0.5 truncate',
                                  isActive 
                                    ? `${colors.primaryText}/90` 
                                    : isDefault 
                                    ? 'text-green-700 dark:text-green-300' 
                                    : colors.textMuted
                                )}
                              >
                                {preset.description}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* Preset Summary */}
                        <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200/50 dark:border-gray-700/50">
                          <div className={cn(
                            'text-[10px] sm:text-xs flex items-center',
                            isActive 
                              ? 'text-white/70' 
                              : isDefault 
                              ? 'text-green-600 dark:text-green-400' 
                              : 'text-gray-500 dark:text-gray-400'
                          )}>
                            <FilterIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                            <span>
                              {getFilterSummary(preset.filters)} • {new Date(preset.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0 w-full sm:w-auto justify-end sm:justify-start">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleApplyPreset(preset)}
                          disabled={isActive}
                          className={cn(
                            'h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs font-medium',
                            isActive
                              ? `${colors.primaryText}/90 hover:${colors.primaryText} hover:bg-white/20 border border-white/30`
                              : isDefault
                              ? 'text-green-700 hover:text-green-900 hover:bg-green-100 dark:text-green-300 dark:hover:text-green-100 dark:hover:bg-green-800/30 border border-green-300 dark:border-green-700'
                              : 'text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800'
                          )}
                        >
                          <CheckIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
                          {isActive ? 'Applied' : 'Apply'}
                        </Button>
                        {!isDefault && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSetAsDefault(preset.id)}
                            disabled={isLoading}
                            className={cn(
                              'h-7 sm:h-8 px-2 sm:px-2.5 text-[10px] sm:text-xs font-medium',
                              isActive
                                ? 'text-yellow-200 hover:text-yellow-100 hover:bg-white/20'
                                : 'text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                            )}
                            title="Set as default preset"
                          >
                            <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-0.5 sm:mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            <span className="hidden sm:inline">Default</span>
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEditPreset(preset)}
                          className={cn(
                            'h-7 sm:h-8 px-2 sm:px-2.5 text-[10px] sm:text-xs font-medium',
                            isActive
                              ? `${colors.primaryText}/90 hover:${colors.primaryText} hover:bg-white/20`
                              : isDefault
                              ? 'text-green-700 hover:text-green-900 hover:bg-green-100 dark:text-green-300 dark:hover:text-green-100 dark:hover:bg-green-800/30'
                              : `${colors.textSecondary} hover:${colors.text} hover:${colors.surfaceHover}`
                          )}
                        >
                          <PencilIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => confirmDeletePreset(preset)}
                          className={cn(
                            'h-7 sm:h-8 px-2 sm:px-2.5 text-[10px] sm:text-xs font-medium',
                            isActive
                              ? 'text-red-300 hover:text-red-100 hover:bg-red-500/20'
                              : isDefault
                              ? 'text-red-700 hover:text-red-900 hover:bg-red-100 dark:text-red-300 dark:hover:text-red-100 dark:hover:bg-red-800/30'
                              : 'text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'
                          )}
                          title="Delete preset"
                        >
                          <TrashIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-900/30 rounded-lg sm:rounded-xl p-3 sm:p-4 mt-4">
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="p-1 sm:p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
                <InfoIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h5 className="text-[10px] sm:text-xs font-semibold text-blue-900 dark:text-blue-300 mb-1.5 sm:mb-2">Tips & Instructions</h5>
                <ul className="space-y-1 sm:space-y-1.5 text-[10px] sm:text-xs text-blue-800 dark:text-blue-400">
                  <li className="flex items-start gap-1.5 sm:gap-2">
                    <span className="text-blue-500 mt-0.5 flex-shrink-0">•</span>
                    <span>Filter presets save your current filter combinations</span>
                  </li>
                  <li className="flex items-start gap-1.5 sm:gap-2">
                    <span className="text-blue-500 mt-0.5 flex-shrink-0">•</span>
                    <span>Apply filters first, then save them as a preset</span>
                  </li>
                  <li className="flex items-start gap-1.5 sm:gap-2">
                    <span className="text-blue-500 mt-0.5 flex-shrink-0">•</span>
                    <span>Click ⭐ to set a preset as default</span>
                  </li>
                  <li className="flex items-start gap-1.5 sm:gap-2">
                    <span className="text-blue-500 mt-0.5 flex-shrink-0">•</span>
                    <span>Default presets load automatically when page opens</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Preset Modal - z-[110] so it appears above the Filter Presets panel (z-[100]) */}
      {showSaveModal && (
        <Modal isOpen={showSaveModal} zIndex={110} onClose={() => {
          setShowSaveModal(false);
          setPresetName('');
          setPresetDescription('');
          setShowInQuickFilters(false);
          setEditingPreset(null);
        }}>
          <ModalHeader>
            <ModalTitle>{editingPreset ? 'Edit Filter Preset' : 'Save Filter Preset'}</ModalTitle>
          </ModalHeader>
          <ModalContent>
            <div className="space-y-4">
              <div>
                <label className={cn('block text-sm font-medium mb-1', colors.text)}>
                  Preset Name *
                </label>
                <TextInput
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder="e.g., Active Clients Only"
                  name="presetName"
                />
              </div>

              <div>
                <label className={cn('block text-sm font-medium mb-1', colors.text)}>
                  Description (optional)
                </label>
                <TextInput
                  value={presetDescription}
                  onChange={(e) => setPresetDescription(e.target.value)}
                  placeholder="e.g., Shows only active clients in operation"
                  name="presetDescription"
                />
              </div>

              {/* Show in Quick Filters Toggle */}
              <div className={cn('flex items-center justify-between p-3 rounded border', colors.surface, colors.border)}>
                <div className="flex-1">
                  <label className={cn('block text-sm font-medium mb-1', colors.text)}>
                    Show in Quick Filters
                  </label>
                  <p className={cn('text-xs', colors.textSecondary)}>
                    Display this preset as a quick filter button above the filter panel
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showInQuickFilters}
                    onChange={(e) => setShowInQuickFilters(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                </label>
              </div>

              {/* Show what will be saved */}
              <div className={cn('p-3 rounded border', colors.surface, colors.border)}>
                <h4 className={cn('text-sm font-medium mb-2', colors.text)}>Filters to save:</h4>
                <div className={cn('text-xs', colors.textSecondary)}>{getFilterSummary(getCurrentFilters())}</div>
              </div>
            </div>
          </ModalContent>
          <ModalFooter>
            <Button variant="outline" onClick={() => {
              setShowSaveModal(false);
              setPresetName('');
              setPresetDescription('');
              setShowInQuickFilters(false);
              setEditingPreset(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleSavePreset} disabled={!presetName.trim()}>
              {editingPreset ? 'Update Preset' : 'Save Preset'}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      <ConfirmationDialog
        isOpen={showDeleteConfirm && !!presetToDelete}
        onClose={cancelDelete}
        onConfirm={handleDeletePreset}
        title="Delete Filter Preset"
        message={presetToDelete ? `Are you sure you want to delete the filter preset "${presetToDelete.name}"?` : ''}
        details={presetToDelete ? {
          Filters: getFilterSummary(presetToDelete.filters),
          Created: new Date(presetToDelete.createdAt).toLocaleDateString(),
          ...(presetToDelete.description ? { Description: presetToDelete.description } : {}),
        } : undefined}
        warningMessage="This action cannot be undone."
        confirmText="Delete Preset"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={isLoading}
        zIndex={110}
      />
    </>
  );
};

export default FilterPresets;

